import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { usePage } from "@inertiajs/vue3";
import { computed } from "vue";
import type { Component } from "vue";
import type { MaybeRef } from "@vueuse/core";
import { useTranslations } from "@/composables/useTranslations";
import route from "ziggy-js";
import { ShieldAlert, Grid3x3, Code } from "lucide-vue-next";

import {
    UserIcon,
    UsersIcon,
    SpeakerWaveIcon,
    IdentificationIcon,
    GlobeAltIcon,
    ArchiveBoxXMarkIcon,
    WrenchScrewdriverIcon,
    ChartBarIcon,
    ExclamationTriangleIcon,
    CalendarDaysIcon,
    CurrencyDollarIcon,
    VariableIcon,
    RocketLaunchIcon,
    ChatBubbleLeftRightIcon,
    SparklesIcon,
    CpuChipIcon,
    HandThumbUpIcon,
} from "@heroicons/vue/24/outline";

export interface ILink {
    name: string;
    href: string;
    icon: Component;
    count?: MaybeRef<number>;
    badge?: string;
    id: string;
    tooltip?: string;
    locked?: boolean;
    // Extra URL prefixes that also mark this item active (e.g. sibling sections
    // belonging to the same menu). Defaults to just `id` when omitted.
    matchPrefixes?: string[];
}

export interface ILinkGroup {
    name: string;
    icon: Component;
    id: string;
    children: ILink[];
}

export type ISidebarItem = ILink | ILinkGroup;

export const isLinkGroup = (item: ISidebarItem): item is ILinkGroup => {
    return "children" in item;
};

export default () => {
    const page = computed(() => usePage<IRhizomPageProps>().url);
    const { t } = useTranslations();
    const globalProps = computed(() => usePage<IRhizomPageProps>().props);

    const linksApp = computed((): ILink[] => {
        const links: ILink[] = [
            {
                name: t("vue.dashboard"),
                href: route("home"),
                icon: Grid3x3,
                id: "/dashboard",
            },
            {
                name: t("vue.domains"),
                href: route("domains.index"),
                icon: GlobeAltIcon,
                id: "/domains",
            },
            {
                name: t("vue.reports"),
                href: route("reports.index"),
                icon: ChartBarIcon,
                id: "/reports",
            },
            {
                name: t("vue.blacklists"),
                href: route("blacklists.index"),
                icon: ArchiveBoxXMarkIcon,
                id: "/blacklists",
            },
            {
                name: t("vue.alerts"),
                href: route("alerts.overview"),
                icon: ShieldAlert,
                id: "/alerts",
            },
            {
                name: t("vue.dns_tools"),
                href: route("dns-tools.index"),
                icon: Code,
                id: "/dns-tools",
            },
        ];

        const ai = globalProps.value.aiChat;

        if (ai && ai.level !== "hidden") {
            links.push({
                name: t("vue.ai_chat"),
                href: globalProps.value.inAppAiChatDashboardActive
                    ? route("ai-chat.dashboard.index")
                    : route("ai-chat.index"),
                icon: ChatBubbleLeftRightIcon,
                id: "/ai-chat",
                matchPrefixes: ["/ai-chat", "/ai-content", "/ai-tags"],
                badge: ai.level === "full" ? t("vue.new") : undefined,
                locked: ai.level === "blocked",
                tooltip:
                    ai.level === "blocked"
                        ? t("vue.ai_chat_upsell")
                        : undefined,
            });
        }

        if (globalProps.value.hasFreePlan) {
            const freePlanHiddenIds = new Set(["/alerts"]);
            return links.filter((link) => !freePlanHiddenIds.has(link.id));
        }

        return links;
    });

    const linksKiosk = computed((): ISidebarItem[] => {
        return [
            {
                name: t("vue.users_profiles"),
                href: route("kiosk.users.index"),
                icon: UserIcon,
                id: "/kiosk/users",
            },
            {
                name: t("vue.teams_accounts"),
                href: route("kiosk.teams.index"),
                icon: UsersIcon,
                id: "/kiosk/teams",
            },
            {
                name: t("vue.admins"),
                href: route("kiosk.admins.index"),
                icon: IdentificationIcon,
                id: "/kiosk/admins",
            },
            {
                name: t("vue.announcements"),
                href: route("kiosk.announcements.index"),
                icon: SpeakerWaveIcon,
                id: "/kiosk/announcements",
            },
            {
                name: t("vue.app_env_vars"),
                href: route("kiosk.app-env-vars.index"),
                icon: VariableIcon,
                id: "/kiosk/app-env-vars",
            },
            {
                name: t("vue.changelog"),
                href: route("kiosk.changelog.index"),
                icon: RocketLaunchIcon,
                id: "/kiosk/changelog",
            },
            {
                name: t("vue.alert_logs"),
                href: route("kiosk.alert-logs.index"),
                icon: ExclamationTriangleIcon,
                id: "/kiosk/alert-logs",
            },
            {
                name: t("vue.tools"),
                href: route("kiosk.tools.index"),
                icon: WrenchScrewdriverIcon,
                id: "/kiosk/tools",
            },
            {
                name: t("vue.daily_activity"),
                href: route("kiosk.daily-activity.index"),
                icon: CalendarDaysIcon,
                id: "/kiosk/daily-activity",
            },
            {
                name: t("vue.kiosk_ai_tools"),
                icon: CpuChipIcon,
                id: "/kiosk/ai-tools",
                children: [
                    {
                        name: t("vue.kiosk_ai_spend"),
                        href: route("kiosk.ai-spend.index"),
                        icon: CurrencyDollarIcon,
                        id: "/kiosk/ai-spend",
                    },
                    {
                        name: t("vue.kiosk_ai_skills"),
                        href: route("kiosk.ai-skills.index"),
                        icon: SparklesIcon,
                        id: "/kiosk/ai-skills",
                    },
                    {
                        name: t("vue.kiosk_ai_feedback"),
                        href: route("kiosk.ai-feedback.index"),
                        icon: HandThumbUpIcon,
                        id: "/kiosk/ai-feedback",
                    },
                ],
            },
            // {
            //     name: 'Lambdas',
            //     href: route('kiosk.lambdas.index'),
            //     icon: MusicalNoteIcon,
            //     id: '/kiosk/lambdas',
            // },
        ];
    });
    const links = computed((): ISidebarItem[] => {
        return page.value.startsWith("/kiosk")
            ? linksKiosk.value
            : linksApp.value;
    });

    const isLinkActive = (link: ILink): boolean => {
        const prefixes = link.matchPrefixes ?? [link.id];

        return prefixes.some((prefix) => page.value.startsWith(prefix));
    };

    const isGroupActive = (group: ILinkGroup): boolean => {
        return group.children.some((child) => isLinkActive(child));
    };

    return {
        links,
        isLinkActive,
        isGroupActive,
    };
};
